import React, { useContext, useEffect, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import { PostData } from '../model/types';
import { supabase } from '../supabaseClient';
import TimeDuration from '../TimeDuration/index';

function QuotedPostCard({ postId }: { postId: string }) {
  const { getPost, getUserRecord } = useContext<CacheContextType>(CacheContext);
  const [post, setPost] = useState<PostData | null>(getPost(postId) ?? null);
  const [loading, setLoading] = useState(!post);

  useEffect(() => {
    const cached = getPost(postId);
    if (cached) { setPost(cached); setLoading(false); return; }
    supabase.from('posts').select('*').eq('id', postId).single().then(({ data }) => {
      if (data) setPost(data as PostData);
      setLoading(false);
    });
  }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps

  const author = post ? getUserRecord(post.user_record_id) : null;

  if (loading) {
    return (
      <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Skeleton width="40%" height={16} sx={{ mb: 0.5 }} />
        <Skeleton width="80%" height={14} />
      </Box>
    );
  }

  if (!post) return null;

  const isExpired = post.is_expired || (!post.is_permanent && DateTime.fromISO(post.expire_at) < DateTime.now());
  const expiryDate = DateTime.fromISO(post.expire_at).toJSDate();

  // Strip mention/feed markup for preview text
  const previewText = (post.value ?? '')
    .replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')
    .replace(/#\[([^\]]+)\]\([^)]+\)/g, '#$1')
    .slice(0, 200);

  return (
    <Box
      component={Link}
      to={`/posts/${post.id}`}
      sx={{
        display: 'block',
        mt: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: isExpired ? '#616161' : post.is_permanent ? 'warning.main' : 'divider',
        bgcolor: 'background.default',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'box-shadow 0.15s ease',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
          {author?.username ?? '…'}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          @{author?.username ?? '…'}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>·</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          <TimeDuration jsDate={DateTime.fromISO(post.created_at).toJSDate()} invertSign titleDisplay="absoluteTime" /> ago
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>·</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: isExpired ? 'error.main' : post.is_permanent ? 'warning.main' : 'text.secondary' }}>
          {isExpired ? 'expired' : post.is_permanent ? 'IMMORTAL' : <><TimeDuration jsDate={expiryDate} /> left</>}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: '0.875rem',
          color: isExpired ? 'text.disabled' : 'text.primary',
          fontStyle: isExpired ? 'italic' : 'normal',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {isExpired ? '[this post has expired]' : previewText}
        {!isExpired && (post.value ?? '').length > 200 && '…'}
      </Typography>
    </Box>
  );
}

export default QuotedPostCard;
